import React, { useState, useRef } from 'react';
import { ReactComponent as Logo } from '@/assets/v2/logos/logo.svg';
import { Link } from 'gatsby';
import { Fade as Hamburger } from 'hamburger-react';
import { motion } from 'framer-motion';
import btnBG from '@/assets/patterns/smallcon-btn-bg.svg';
import { ReactComponent as ArrowDown } from '@/assets/v2/icons/arrow-down.svg';
import { Heart } from './Heart';
import navData from '../../../data/v2/navigation';

const Accordion = ({ title, icon, options }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="accordion">
      <button
        className="accordion-title flex items-center !justify-start gap-2 text-start w-full hover:text-orange"
        onClick={toggleAccordion}
        tabIndex={0}
      >
        {icon && (
          <img src={icon} alt="" className="w-8 h-8 rounded-md shadow-[0px_23px_7px_0px_rgba(0,0,0,0.00),0px_15px_6px_0px_rgba(0,0,0,0.01),0px_8px_5px_0px_rgba(0,0,0,0.05),0px_4px_4px_0px_rgba(0,0,0,0.09),0px_1px_2px_0px_rgba(0,0,0,0.10)]" />
        )}
        <span className="font-medium">{title}</span>
        <span
          className={`icon ${
            isOpen ? 'open' : ''
          } hidden lg:inline  stroke-black hover:stroke-[#E25636]`}
        >
          <svg
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className=""
          >
            <path d="M9 5L5 1L1 5" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="accordion-content mt-2.5">
          {options.map((item, jdx) => (
            <motion.li
              initial={{
                opacity: 0,
                translateX: '10px',
              }}
              animate={{
                opacity: isOpen ? 1 : 0,
                translateX: isOpen ? '0px' : '10px',
              }}
              transition={{
                delay: 0.2 * (jdx + 1),
                duration: 0.2,
                ease: 'linear',
              }}
              key={`${item.name.replace(' ', '_')}-sub-${jdx}`}
            >
              {item.url.startsWith('http') ? (
                <div className="flex items-center gap-2">

                  <a
                    href={item.url}
                    className="lg:text-sm font-normal py-2.5 block hover:text-orange"
                  >
                    {item.name}
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Heart />
                  <Link
                    to={item.url}
                    className="lg:text-sm font-normal py-2.5 block hover:text-orange"
                  >
                    {item.name}
                  </Link>
                </div>
              )}
            </motion.li>
          ))}
        </div>
      )}
    </div>
  );
};

const DropdownMenu = ({ item, items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  const handleOpen = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };
  return (
    <div
      className="relative lg:py-2"
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleClose}
    >
      <button
        className="lg:mr-4 xl:mr-8 relative cursor-pointer z-10 flex items-center gap-1"
        tabIndex={0}
      >
        {item}
        <ArrowDown />
      </button>
      <motion.ul
        initial={{ opacity: 0, height: 0, display: 'none' }}
        animate={{
          opacity: isOpen ? 1 : 0,
          height: isOpen ? 'auto' : '0',
          display: isOpen ? 'block' : 'none',
        }}
        className={`lg:absolute -left-4 w-64 top-14 lg:bg-white lg:border lg:border-[#E5E5E5] px-1 py-1.5 z-10 shadow-lg rounded-md`}
      >
        {items.map((i, idx) => {
          return (
            <motion.li
              initial={{
                opacity: 0,
                translateX: '10px',
              }}
              animate={{
                opacity: isOpen ? 1 : 0,
                translateX: isOpen ? '0px' : '10px',
              }}
              key={`${item.replace(' ', '_')}-sub-${idx}`}
              className="px-3 py-2.5 block"
            >
              {i.subitems ? (
                <ul>
                  <Accordion
                    title={i.name}
                    icon={i.icon}
                    options={i.subitems}
                  />
                </ul>
              ) : i.url.startsWith('http') ? (
                <div className="flex items-center gap-2">
                  {i.icon && (
                    <img
                      src={i.icon}
                      alt=""
                      className="w-8 h-8 rounded-md shadow-[0px_23px_7px_0px_rgba(0,0,0,0.00),0px_15px_6px_0px_rgba(0,0,0,0.01),0px_8px_5px_0px_rgba(0,0,0,0.05),0px_4px_4px_0px_rgba(0,0,0,0.09),0px_1px_2px_0px_rgba(0,0,0,0.10)]"
                    />
                  )}
                  <a
                    href={i.url}
                    className="font-medium lg:text-sm hover:text-orange"
                  >
                    {i.name}
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {i.icon && (
                    <img
                      src={i.icon}
                      alt=""
                      className="w-8 h-8 rounded-md shadow-[0px_23px_7px_0px_rgba(0,0,0,0.00),0px_15px_6px_0px_rgba(0,0,0,0.01),0px_8px_5px_0px_rgba(0,0,0,0.05),0px_4px_4px_0px_rgba(0,0,0,0.09),0px_1px_2px_0px_rgba(0,0,0,0.10)]"
                    />
                  )}
                  <Link
                    to={i.url}
                    className="font-medium lg:text-sm hover:text-orange"
                  >
                    {i.name}
                  </Link>
                </div>
              )}
            </motion.li>
          );
        })}
      </motion.ul>
      <div className="absolute left-0 top-0 right-4 -bottom-6 z-0 lg:block hidden" />
    </div>
  );
};

const Header = ({ isAnnouncementVisible }) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const location = typeof window !== 'undefined' ? window.location : null;
  return (
    <header
      className={`${
        isAnnouncementVisible ? 'top-10' : 'top-0'
      } left-0 right-0 z-30 bg-white h-14 fixed lg:h-20 border-b border-[#FFEDE7]`}
    >
      <div className="w-full h-full mx-auto">
        <div
          className={`${
            isAnnouncementVisible ? 'top-10' : 'top-0'
          } xl:container fixed z-40 flex items-center justify-between w-full bg-white lg:bg-transparent lg:transform lg:-translate-x-1/2 h-14 lg:left-1/2 lg:h-20 border-b border-[#FFEDE7] lg:border-none`}
        >
          <div className="ps-4 lg:ps-8">
            <Link aria-label="Predibase Home" to="/">
              <div className="flex items-center h-12 bg-white w-36 xl:w-52">
                <Logo className="w-full h-full" />
              </div>
            </Link>
          </div>
          <ul
            className={`nav-list lg:pe-8 ${
              menuIsOpen ? 'open' : null
            } text-white z-0 fixed flex items-start p-4 text-3xl justify-start flex-col ${
              isAnnouncementVisible ? 'top-24' : 'top-14'
            } bottom-0 left-0 right-0 bg-dark font-body translate-x-full transform lg:translate-x-0 invisible lg:visible lg:static lg:flex-row lg:bg-transparent lg:text-dark lg:text-sm lg:items-center lg:p-0`}
          >
            {navData.map((item) => {
              return (
                <li className="mt-7 lg:mt-0" key={item.name}>
                  {item.submenu ? (
                    <DropdownMenu item={item.name} items={item.subitems} />
                  ) : item.url.startsWith('http') ? (
                    <a href={item.url} className="lg:mr-4 xl:mr-8">
                      {item.name}
                    </a>
                  ) : (
                    <Link to={item.url} className="lg:mr-4 xl:mr-8">
                      {item.name}
                    </Link>
                  )}
                </li>
              );
            })}
            {location?.pathname === '/smallcon' ? (
              <li>
                <a
                  href="#register-now"
                  className="flex justify-center items-center relative mt-7 lg:mt-0 lg:mr-3 overflow-hidden"
                >
                  <span className="text-[#0E0C0C] z-10 uppercase px-4 lg:py-3 py-4 lg:px-6 leading-none">
                    Watch On-demand
                  </span>
                  <img
                    src={btnBG}
                    className="w-full h-full absolute pointer-events-none"
                    alt=""
                  />
                </a>
              </li>
            ) : (
              <>
                <li>
                  <a
                    href="https://app.predibase.com/"
                    className="inline-block px-8 py-4 mt-7 text-2xl duration-300 lg:text-sm text-orange lg:mt-0 lg:py-2 lg:px-6 lg:mr-3"
                  >
                    Sign In
                  </a>
                </li>
                <li>
                  <Link
                    to="/free-trial"
                    activeClassName="activeHeaderCta"
                    className="inline-block px-8 py-4 mt-7 text-2xl transition-colors duration-300 border lg:text-sm text-orange border-orange lg:mt-0 lg:py-2 lg:px-6 hover:bg-orange hover:text-white rounded-full"
                  >
                    Try Predibase Free
                  </Link>
                </li>
              </>
            )}
          </ul>

          <button
            onClick={() => setMenuIsOpen(!menuIsOpen)}
            className="lg:hidden pe-2"
            aria-label="Menu Toggle"
          >
            <Hamburger
              toggled={menuIsOpen}
              toggle={() => setMenuIsOpen(!menuIsOpen)}
              label="Hamburger"
            />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
